import { useEffect, useState, useRef } from 'react'
import { Button } from 'component-library'
import { useCloseOnEscape } from 'shared-utilities'
import DatasetBreadcrumbs from '../DatasetBreadcrumbs/DatasetBreadcrumbs'
import { useInView } from 'react-intersection-observer'
import DownloadForm from '../DownloadForm/DownloadForm'
import ConfigureForm from '../ConfigureForm/ConfigureForm'
import { useOnClickOutside } from 'usehooks-ts'
import './DatasetHero.scss'

interface IDatasetHeroProps {
  title: string
  breadcrumbCategory?: {
    name: string
    url?: string
  }
  hasParameters: boolean
  categorySlug: string
  heroImage: string
}

const DatasetHero = ({
  title,
  heroImage,
  hasParameters,
  breadcrumbCategory,
  categorySlug,
}: IDatasetHeroProps) => {
  const { ref, inView } = useInView()

  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )
  const [isHeroSticky, setIsHeroSticky] = useState<boolean>(false)
  const [isDownloadFormOpen, toggleIsDownloadFormOpen] =
    useState<boolean>(false)
  const [isStickyDownloadFormOpen, toggleIsStickyDownloadFormOpen] =
    useState<boolean>(false)
  const [isConfigureFormOpen, toggleIsConfigureFormOpen] =
    useState<boolean>(false)
  const [isStickyConfigureFormOpen, toggleIsStickyConfigureFormOpen] =
    useState<boolean>(false)

  // Refs
  const downloadFormRef = useRef(null)
  const stickyDownloadFormRef = useRef(null)
  const configureFormRef = useRef(null)
  const stickyConfigureFormRef = useRef(null)

  /* Outside click handlers */
  useOnClickOutside(downloadFormRef, () => toggleIsDownloadFormOpen(false))
  useOnClickOutside(stickyDownloadFormRef, () =>
    toggleIsStickyDownloadFormOpen(false)
  )
  useCloseOnEscape(isDownloadFormOpen, toggleIsDownloadFormOpen)
  useCloseOnEscape(isStickyDownloadFormOpen, toggleIsStickyDownloadFormOpen)
  useOnClickOutside(configureFormRef, () => toggleIsConfigureFormOpen(false))
  useOnClickOutside(stickyConfigureFormRef, () =>
    toggleIsStickyConfigureFormOpen(false)
  )

  useCloseOnEscape(isConfigureFormOpen, toggleIsConfigureFormOpen)
  useCloseOnEscape(isStickyConfigureFormOpen, toggleIsStickyConfigureFormOpen)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleWindowResize = () => {
        setWindowWidth(window.innerWidth)
      }
      window.addEventListener('resize', handleWindowResize)
      return () => {
        window.removeEventListener('resize', handleWindowResize)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsHeroSticky(windowWidth >= 1280 && !inView)
    }
  }, [windowWidth, inView])

  useEffect(() => {
    toggleIsDownloadFormOpen(isHeroSticky && isDownloadFormOpen)
    toggleIsStickyDownloadFormOpen(!isHeroSticky && isStickyDownloadFormOpen)
    toggleIsConfigureFormOpen(isHeroSticky && isConfigureFormOpen)
    toggleIsStickyConfigureFormOpen(!isHeroSticky && isStickyConfigureFormOpen)
  }, [isHeroSticky])

  return (
    <>
      <section
        className={`DatasetHero--sticky ${
          isHeroSticky ? 'visible top-12' : 'invisible -top-[100px]'
        }`}
        data-testid="dataset-hero-sticky"
      >
        <div className="container flex justify-between items-center">
          <DatasetBreadcrumbs
            name={title}
            categoryByCategorySlug={breadcrumbCategory}
            className="flex capitalize"
          />
          <div>
            <h2 className="DatasetHero__title">{title}</h2>
          </div>
          <div className="DatasetHero__buttons">
            {hasParameters && (
              <div className="relative" ref={stickyConfigureFormRef}>
                <Button
                  className="DatasetHero__button"
                  label="Configure"
                  type="secondary"
                  leftIcon={<i className="bx bx-slider-alt"></i>}
                  onClick={() =>
                    toggleIsStickyConfigureFormOpen(!isStickyConfigureFormOpen)
                  }
                  aria-expanded={isStickyConfigureFormOpen}
                />
                <ConfigureForm
                  category={categorySlug}
                  name={title}
                  isVisible={isStickyConfigureFormOpen && isHeroSticky}
                />
              </div>
            )}
            <div className="relative" ref={stickyDownloadFormRef}>
              <Button
                className="DatasetHero__button"
                label="Download"
                rightIcon={<i className="bx bx-chevron-down"></i>}
                onClick={() =>
                  toggleIsStickyDownloadFormOpen(!isStickyDownloadFormOpen)
                }
                aria-expanded={isStickyDownloadFormOpen}
              />
              <DownloadForm
                category={categorySlug}
                isVisible={isStickyDownloadFormOpen && isHeroSticky}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="DatasetHero" data-testid="dataset-hero">
        <div className="flex justify-between">
          <div>
            <h1 className="DatasetHero__title">{title}</h1>
          </div>
          <div className="DatasetHero__buttons" ref={ref}>
            {hasParameters && (
              <div className="relative" ref={configureFormRef}>
                <Button
                  label="Configure"
                  type="secondary"
                  className="DatasetHero__button"
                  leftIcon={<i className="bx bx-slider-alt"></i>}
                  onClick={() =>
                    toggleIsConfigureFormOpen(!isConfigureFormOpen)
                  }
                  aria-expanded={isConfigureFormOpen}
                />
                <ConfigureForm
                  category={categorySlug}
                  name={title}
                  isVisible={isConfigureFormOpen && !isHeroSticky}
                />
              </div>
            )}
            <div className="relative" ref={downloadFormRef}>
              <Button
                label="Download"
                className="DatasetHero__button"
                rightIcon={<i className="bx bx-chevron-down"></i>}
                onClick={() => toggleIsDownloadFormOpen(!isDownloadFormOpen)}
                aria-expanded={isDownloadFormOpen}
              />
              <DownloadForm
                category={categorySlug}
                isVisible={isDownloadFormOpen && !isHeroSticky}
              />
            </div>
          </div>
        </div>
        <div className="mb-2 md:mb-10 md:min-h-[168px] lg:min-h-[330px]">
          <img
            src={heroImage}
            alt={`${title} dataset visualization`}
            data-testid="hero-image"
            className="w-full"
            loading="eager"
            // fetchpriority is supported in TypeScript via custom.d.ts declaration its a valid attribute just not supported by the type system.
            // eslint-disable-next-line react/no-unknown-property
            fetchpriority="high"
            width="1200"
            height="330"
          />
        </div>
      </section>
    </>
  )
}

export default DatasetHero
